import { Box, InputAdornment, TextField, TextFieldProps } from '@mui/material';
import CustomLabel from 'components/common/CustomLabel/CustomLabel';
import { ReactNode } from 'react';
import { FieldError, UseFormRegister } from 'react-hook-form';
import { customTextFieldSlotProps, customTextFieldSx } from 'styles/customTextFieldStyles';
import useGlobalStyles from 'styles/globalStyles';


type CustomFormTextInputProps = {
  name: string;
  register: UseFormRegister<any>;
  error?: FieldError;
  placeholder?: string;
  label?: string;
  required?: boolean;
  note?: string;
  floatingLabel?: string;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  editable?: boolean;
} & Omit<TextFieldProps, 'name' | 'error' | 'helperText' | 'editible'>;

export default function CustomFormTextInput({
  name,
  register,
  error,
  placeholder,
  label,
  required,
  note,
  sx,
  floatingLabel,
  startIcon,
  endIcon,
  editable = true,
  ...rest
}: CustomFormTextInputProps) {

  const globalStyles = useGlobalStyles();

  return (
    <Box>

      {/* Label */}
      {label && <CustomLabel
        label={label}
        note={note}
        required={required} />}

      {/* Text Field Container */}
      <Box position='relative'>

        {/* Text Field */}
        <TextField
          {...register(name)}
          placeholder={placeholder}
          size="small"
          fullWidth
          label={floatingLabel}
          error={!!error}
          helperText={error?.message}
          slotProps={{
            ...customTextFieldSlotProps,
            htmlInput: {
              sx: {
                fontSize: '15px',
              },
            },
            input: {
              readOnly: !editable,
              notched: true,
              endAdornment: endIcon ? (
                <InputAdornment position="end">
                  {endIcon}
                </InputAdornment>
              ) : undefined,
              startAdornment: startIcon ? (
                <InputAdornment position="start">
                  {startIcon}
                </InputAdornment>
              ) : undefined,
            },
            inputLabel: {
              shrink: true,
              required: required,
              className: globalStyles.floatingLabelText,
            },
          }}
          sx={{
            ...(startIcon && {
              '& .MuiOutlinedInput-root.MuiInputBase-adornedStart .MuiOutlinedInput-input':
              {
                paddingLeft: 0.5,
              },
            }),

            ...(endIcon && {
              '& .MuiOutlinedInput-root.MuiInputBase-adornedEnd': {
                paddingRight: 1,
              },
            }),
            ...customTextFieldSx,
            ...sx
          }}
          {...rest}
        />
      </Box>
    </Box>
  );
}
